import React from 'react';
import { YStack } from 'tamagui';
import { CobuText } from '@cobu/ui';
import type { ModerationFlaggedTerm } from '@cobu/types';
import { Strings } from '../../constants';

const FLAG_COLOR = '#e5484d';

interface FlaggedTermsPreviewProps {
  /** The text the user typed — the offsets in `flaggedTerms` index into this. */
  contents: string;
  flaggedTerms: ModerationFlaggedTerm[];
}

/**
 * Echoes the user's text back with the server's blocked terms highlighted, so a
 * PROFANITY rejection points at *which* words to change instead of just saying
 * "no". Shared by the Feed and Places compose screens.
 *
 * The server sends start/end offsets, but they can go stale the moment the user
 * edits the text, so each term falls back to a case-insensitive search for its
 * `match` string when the offsets no longer fit `contents`.
 */
export default function FlaggedTermsPreview({ contents, flaggedTerms }: FlaggedTermsPreviewProps) {
  if (!contents || !flaggedTerms || flaggedTerms.length === 0) return null;

  const sortedRanges = flaggedTerms
    .map(term => {
      if (!term) return null;
      const hasValidOffsets =
        Number.isFinite(term.start) &&
        Number.isFinite(term.end) &&
        term.start >= 0 &&
        term.end > term.start &&
        term.end <= contents.length;

      if (hasValidOffsets) {
        return { start: term.start, end: term.end };
      }

      if (!term.match) return null;
      const matchIndex = contents.toLowerCase().indexOf(term.match.toLowerCase());
      if (matchIndex < 0) return null;
      return { start: matchIndex, end: matchIndex + term.match.length };
    })
    .filter((range): range is { start: number; end: number } => !!range)
    .sort((a, b) => a.start - b.start);

  if (sortedRanges.length === 0) return null;

  // Overlapping matches (e.g. a term and a phrase containing it) would otherwise
  // render nested spans and double-count the text, so collapse them first.
  const mergedRanges = sortedRanges.reduce<Array<{ start: number; end: number }>>((acc, range) => {
    const last = acc[acc.length - 1];
    if (!last || range.start > last.end) {
      acc.push({ ...range });
    } else {
      last.end = Math.max(last.end, range.end);
    }
    return acc;
  }, []);

  const segments: React.ReactNode[] = [];
  let cursor = 0;
  mergedRanges.forEach((range, idx) => {
    if (cursor < range.start) {
      segments.push(
        <span key={`plain-${idx}`} style={{ color: '#333' }}>
          {contents.slice(cursor, range.start)}
        </span>
      );
    }
    segments.push(
      <span key={`flagged-${idx}`} style={{ color: FLAG_COLOR, fontWeight: 700, textDecoration: 'underline' }}>
        {contents.slice(range.start, range.end)}
      </span>
    );
    cursor = range.end;
  });

  if (cursor < contents.length) {
    segments.push(
      <span key="plain-tail" style={{ color: '#333' }}>
        {contents.slice(cursor)}
      </span>
    );
  }

  return (
    <YStack mt="$2" p="$2" boc={FLAG_COLOR} bw="$0.5" br="$3" bg="#fff1f0">
      <CobuText fos="$2" col={FLAG_COLOR} fow="700" mb="$1">
        {Strings.get().discussion.moderationAlerts.flaggedTermsHeader}
      </CobuText>
      <p style={{ margin: 0, fontSize: 14, lineHeight: '20px', flexWrap: 'wrap' }}>
        {segments}
      </p>
    </YStack>
  );
}
